import { useCallback, useEffect, useRef, useState } from 'react';
import { getAuth } from 'firebase/auth';
import { io } from 'socket.io-client';
import { API_URL } from '../constants/string-constants';

/**
 * useSocket - owns the Socket.IO connection lifecycle for the room.
 *
 * Design goals (replaces the old hand-rolled reconnect logic in RoomV2):
 *  - ONE socket instance per identity. We do NOT tear down and rebuild the
 *    socket on transient errors. Socket.IO's built-in reconnection is the
 *    single source of truth for retries/backoff.
 *  - Firebase ID tokens expire after ~1h. Instead of rebuilding the socket
 *    with a fresh token, we mutate `socket.auth.token` in place before each
 *    reconnect attempt (the supported Socket.IO pattern). The next attempt
 *    sends the refreshed token automatically.
 *  - Recovery from iOS Safari tab suspension: when the tab becomes visible
 *    again, or the browser comes back online, if the socket is disconnected
 *    we refresh the token and call socket.connect() on the SAME instance.
 *  - A `connectionState` is exposed so the UI can show a "reconnecting" state
 *    and so sends can be blocked while down (no more silent dropped emits).
 *
 * Event handlers (chat message, reactions, mod events, etc.) live in the
 * caller and are passed in via `handlers`; this hook only owns connection
 * lifecycle and attaches/detaches those handlers.
 */

export const CONNECTION_STATES = {
  IDLE: 'idle',
  CONNECTING: 'connecting',
  CONNECTED: 'connected',
  RECONNECTING: 'reconnecting',
  DISCONNECTED: 'disconnected',
  BLOCKED: 'blocked', // e.g. VPN block - do not auto-reconnect
};

// Refresh the current Firebase user's ID token. Returns 'Bearer <token>' or null.
async function getFreshAuthToken() {
  const auth = getAuth();
  if (!auth.currentUser) return null;
  try {
    const token = await auth.currentUser.getIdToken();
    return 'Bearer ' + token;
  } catch (err) {
    console.error('[useSocket] Failed to refresh ID token:', err);
    return null;
  }
}

function buildAuth(bearerToken) {
  const auth = { token: bearerToken };
  const sessionToken = sessionStorage.getItem('captcha_session_token');
  if (sessionToken) {
    auth.sessionToken = sessionToken;
  } else {
    console.warn('[useSocket] Connecting without captcha session token');
  }
  return auth;
}

/**
 * @param {object} params
 * @param {object} params.handlers   map of eventName -> handler fn (stable via ref internally)
 * @param {function} params.onConnect    called on every (re)connect, after lifecycle setup
 * @param {function} params.onConnectError  optional, called with the error
 * @param {function} params.onBlocked    called when server blocks the connection (e.g. VPN)
 */
export default function useSocket({
  handlers,
  onConnect,
  onConnectError,
  onBlocked,
} = {}) {
  const socketRef = useRef(null);
  const [connectionState, setConnectionState] = useState(
    CONNECTION_STATES.IDLE
  );
  // Track whether the server explicitly blocked us so visibility/online
  // recovery doesn't fight a deliberate disconnect.
  const blockedRef = useRef(false);

  // Keep the latest callbacks in refs so the attached listeners never go stale
  // and we don't need to re-create the socket when a callback identity changes.
  const handlersRef = useRef(handlers);
  const onConnectRef = useRef(onConnect);
  const onConnectErrorRef = useRef(onConnectError);
  const onBlockedRef = useRef(onBlocked);
  useEffect(() => {
    handlersRef.current = handlers;
    onConnectRef.current = onConnect;
    onConnectErrorRef.current = onConnectError;
    onBlockedRef.current = onBlocked;
  });

  // Attach all caller-provided event handlers to a socket instance.
  const attachHandlers = useCallback((socket) => {
    const map = handlersRef.current || {};
    Object.entries(map).forEach(([event, fn]) => {
      if (typeof fn === 'function') {
        // Wrap so the latest handler from the ref is always used.
        socket.on(event, (...args) => {
          const current = handlersRef.current?.[event];
          if (current) current(...args);
        });
      }
    });
  }, []);

  /**
   * Connect for a given identity. Builds the socket ONCE. If a socket already
   * exists we reuse it (refresh token + connect) instead of rebuilding, unless
   * `forceNew` is set (identity change: anon -> Google, account sign-in, etc.).
   */
  const connect = useCallback(
    async ({ forceNew = false } = {}) => {
      const bearer = await getFreshAuthToken();
      if (!bearer) {
        console.warn('[useSocket] connect() with no authenticated user');
        return null;
      }

      blockedRef.current = false;

      // Identity change: dispose the old socket entirely and start fresh.
      if (forceNew && socketRef.current) {
        socketRef.current.removeAllListeners();
        socketRef.current.disconnect();
        socketRef.current = null;
      }

      // Reuse an existing socket: just refresh auth and (re)connect it.
      if (socketRef.current) {
        socketRef.current.auth = buildAuth(bearer);
        if (!socketRef.current.connected) {
          setConnectionState(CONNECTION_STATES.CONNECTING);
          socketRef.current.connect();
        }
        return socketRef.current;
      }

      // First connection for this identity.
      setConnectionState(CONNECTION_STATES.CONNECTING);
      const socket = io(API_URL, {
        auth: buildAuth(bearer),
        reconnection: true,
        reconnectionAttempts: Infinity, // keep trying; backoff handles spacing
        reconnectionDelay: 1000,
        reconnectionDelayMax: 30000,
        randomizationFactor: 0.5,
        timeout: 10000,
      });
      socketRef.current = socket;

      // --- Connection lifecycle (owned here) ---

      // Refresh the token in place before reconnect attempts so an expired
      // Firebase token doesn't cause a permanent auth failure. NOTE: Socket.IO
      // does not await this async handler, so if getIdToken() has to hit the
      // network the FIRST attempt after expiry may still send the stale token;
      // the connect_error handler below refreshes again and the NEXT attempt
      // succeeds. This is best-effort by design, not airtight.
      socket.io.on('reconnect_attempt', async () => {
        setConnectionState(CONNECTION_STATES.RECONNECTING);
        const fresh = await getFreshAuthToken();
        if (fresh) socket.auth = buildAuth(fresh);
      });

      socket.on('connect', () => {
        setConnectionState(CONNECTION_STATES.CONNECTED);
        if (onConnectRef.current) onConnectRef.current(socket);
      });

      socket.on('disconnect', (reason) => {
        if (blockedRef.current) return;
        // 'io server disconnect' means the server kicked us; Socket.IO will
        // NOT auto-reconnect in that case, so we surface disconnected state.
        // For all transport drops, Socket.IO retries -> show reconnecting.
        if (reason === 'io server disconnect') {
          setConnectionState(CONNECTION_STATES.DISCONNECTED);
        } else {
          setConnectionState(CONNECTION_STATES.RECONNECTING);
        }
      });

      socket.on('connect_error', async (err) => {
        // Do NOT rebuild the socket here (that was the old dual-engine bug).
        // Refresh the token in place; Socket.IO's reconnection loop retries.
        // Keep showing CONNECTING during the very first connect attempt so the
        // UI doesn't flash "reconnecting" before we've ever connected; any
        // failure after that is a reconnect.
        setConnectionState((prev) =>
          prev === CONNECTION_STATES.CONNECTING
            ? CONNECTION_STATES.CONNECTING
            : CONNECTION_STATES.RECONNECTING
        );
        const fresh = await getFreshAuthToken();
        if (fresh) socket.auth = buildAuth(fresh);
        if (onConnectErrorRef.current) onConnectErrorRef.current(err);
      });

      // Server explicitly blocked this connection (e.g. VPN). Stop retrying.
      socket.on('connection_blocked', (data) => {
        blockedRef.current = true;
        socket.io.reconnection(false);
        setConnectionState(CONNECTION_STATES.BLOCKED);
        if (onBlockedRef.current) onBlockedRef.current(data);
      });

      // Server matched this device against an enforced fingerprint ban and is
      // about to kick us. Same treatment as connection_blocked: stop retrying
      // (every reconnect would be kicked again) and surface the ban to the
      // caller. Server payload: { reason: <human text>, unban_date }.
      socket.on('fingerprint_banned', (data) => {
        blockedRef.current = true;
        socket.io.reconnection(false);
        setConnectionState(CONNECTION_STATES.BLOCKED);
        if (onBlockedRef.current) {
          onBlockedRef.current({
            reason: 'FINGERPRINT_BAN',
            message: data?.reason,
            unban_date: data?.unban_date,
          });
        }
      });

      // --- Caller event handlers ---
      attachHandlers(socket);

      return socket;
    },
    [attachHandlers]
  );

  // Recover the connection after iOS suspends/freezes a backgrounded tab, or
  // after the network drops and comes back. We reuse the SAME socket instance.
  const recoverIfNeeded = useCallback(async () => {
    const socket = socketRef.current;
    if (!socket) return;
    if (blockedRef.current) return; // deliberate block - don't fight it
    if (socket.connected) return; // already healthy
    const fresh = await getFreshAuthToken();
    if (fresh) socket.auth = buildAuth(fresh);
    setConnectionState(CONNECTION_STATES.RECONNECTING);
    // If Socket.IO is already mid-backoff, calling connect() here just fires an
    // immediate attempt instead of waiting out the timer — which is exactly what
    // we want when the tab wakes or the network returns. It's a no-op if already
    // connecting, so the two recovery paths don't conflict.
    socket.connect();
  }, []);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') recoverIfNeeded();
    };
    const onOnline = () => recoverIfNeeded();
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('online', onOnline);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('online', onOnline);
    };
  }, [recoverIfNeeded]);

  // Tear down on unmount.
  useEffect(() => {
    return () => {
      if (socketRef.current) {
        socketRef.current.removeAllListeners();
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, []);

  const isConnected = connectionState === CONNECTION_STATES.CONNECTED;

  // Safe emit: returns true if sent, false if the socket is down (so callers
  // can show a "reconnecting" state instead of silently dropping the message).
  const safeEmit = useCallback(
    (event, ...args) => {
      const socket = socketRef.current;
      if (socket && socket.connected) {
        socket.emit(event, ...args);
        return true;
      }
      // Kick off recovery so the user's next attempt is more likely to land.
      recoverIfNeeded();
      return false;
    },
    [recoverIfNeeded]
  );

  return {
    socketRef,
    connectionState,
    isConnected,
    connect,
    recoverIfNeeded,
    safeEmit,
  };
}
